// Lib
import React, { useCallback, useEffect, useRef } from 'react';
import { AlertTriangle, X } from 'lucide-react';

// Hooks
import useToast from '@hooks/useToast';

// Utils
import { cn } from '@components/lib/utils';

const TOAST_TIMEOUT = 4000;

interface ToastItemProps {
  id: number;
  title: string;
  message: string;
  onHide: (id: number) => void;
}

const ToastItemComponent: React.FC<ToastItemProps> = ({
  id,
  title,
  message,
  onHide,
}) => {
  const [isExiting, setIsExiting] = React.useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>(null);

  const startExit = useCallback(() => {
    setIsExiting(true);
    setTimeout(() => onHide(id), 300);
  }, [id, onHide]);

  useEffect(() => {
    timerRef.current = setTimeout(startExit, TOAST_TIMEOUT);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [startExit]);

  const handleClose = (e: React.MouseEvent): void => {
    e.stopPropagation();
    if (timerRef.current) clearTimeout(timerRef.current);
    startExit();
  };

  return (
    <div
      className={cn(
        'group relative flex min-w-[280px] max-w-[400px] gap-2 rounded-2xl border border-marble-red-100 bg-marble-red-50 p-3 duration-300 fill-mode-forwards',
        isExiting
          ? 'animate-out fade-out slide-out-to-bottom'
          : 'animate-in fade-in slide-in-from-bottom',
      )}
    >
      <AlertTriangle className="size-5 shrink-0 text-marble-red-600" />
      <div className="flex flex-1 flex-col gap-1">
        <p className="text-sm font-bold text-marble-red-700">{title}</p>
        <p className="text-sm text-marble-red-500">{message}</p>
      </div>
      <button
        type="button"
        onClick={handleClose}
        aria-label="Dismiss notification"
        className="absolute -top-2 -right-2 flex size-6 cursor-pointer items-center justify-center rounded-full bg-marble-red-100 opacity-0 transition-opacity group-hover:opacity-100"
      >
        <X className="size-4 text-marble-red-600" />
      </button>
    </div>
  );
};

const Toast: React.FC = () => {
  const { toasts, hideToast } = useToast();

  if (toasts.length === 0) return null;

  return (
    <div className="fixed bottom-[10px] left-[82px] z-[1002] flex flex-col gap-2 max-[1279px]:bottom-[90px] max-[1279px]:left-[10px]">
      {toasts.map((toast) => (
        <ToastItemComponent
          key={toast.id}
          id={toast.id}
          title={toast.title}
          message={toast.message}
          onHide={hideToast}
        />
      ))}
    </div>
  );
};

export default Toast;
